import { LoadingSpinner } from '@clay-run/glaze/loading-spinner';
import { StatusBadge, type StatusBadgeStatus } from '@clay-run/glaze/status-badge';
import type { RequestSendAttempt, RequestStatus } from '@shared/flow/requests';

/**
 * Small formatting/presentation helpers shared between `RequestsTab`'s
 * collapsed rows and `RequestDetail`'s header (U6) — kept in their own file
 * (not duplicated in each component) since both need the exact same
 * status→badge mapping and kind→icon mapping.
 */

export function formatRelativeTime(iso: string): string {
  const ts = new Date(iso).getTime();
  if (Number.isNaN(ts)) return iso;
  const s = Math.floor((Date.now() - ts) / 1000);
  if (s < 8) return 'just now';
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export function formatAbsoluteDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} · ${d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
}

const STATUS_TO_BADGE: Record<RequestStatus, StatusBadgeStatus> = {
  pending: 'neutral', queued: 'neutral', running: 'action', complete: 'success', failed: 'error',
};
const UNAVAILABLE_TOOLTIP = 'Clay no longer has a result for this run — it may have completed. Verify before re-running.';

/**
 * `handler_result.code` for a `dispatching` row that expired because Clay no
 * longer has an answer for it (HTTP 404 on an over-age row — see
 * execution-handlers/clay-routine.ts's `ClayPollOutcome` and
 * request-reconcile.ts's `failExpiredRow`, 2026-08-13 false-expiry fix).
 * Distinct from a genuine `CLAY_ROUTINE_EXPIRED` ("still reported in
 * progress, presumed stuck"): here the run's true outcome is simply unknown
 * — it may well have succeeded (and had side effects) before Clay's result
 * retention window elapsed. Presentation-layer only (Key Technical Decision
 * #5) — no new `RequestStatus`/`DeliveryStatus` member exists for this.
 */
export const OUTCOME_UNAVAILABLE_CODE = 'CLAY_ROUTINE_RESULT_UNAVAILABLE';

/** Safely reads `.code` off an unknown `response` (the parsed HandlerResult
 * envelope from `RequestEntry.response`, or null for an in-flight/degraded
 * row) — never throws on a malformed/absent shape. */
export function extractResponseCode(response: unknown): string | null {
  if (response && typeof response === 'object' && 'code' in response) {
    const code = (response as { code?: unknown }).code;
    return typeof code === 'string' ? code : null;
  }
  return null;
}

/** True when a `failed` row's real story is "Clay no longer has an answer,"
 * not "this genuinely failed" — the case `RequestStatusBadge`/`RequestDetail`
 * must render as "unknown," never as a plain failure. */
export function isOutcomeUnavailable(status: RequestStatus, code: string | null): boolean {
  return status === 'failed' && code === OUTCOME_UNAVAILABLE_CODE;
}

/**
 * `EnrollmentSendAttempt.status` → the feed-facing `RequestStatus` whose
 * BADGE STYLING says the same thing (U12). Colour only — the badge keeps the
 * attempt's own word as its label (see `RequestStatusBadge`'s `label` prop):
 * telling a rep an attempt is "complete" when the row says `already_in_flow`
 * would erase the one fact that attempt exists to report.
 *
 * `already_in_flow`/`cancelled` map to `null`: neither is a success and
 * neither is a failure (Gong already had the contact; a rep reset a stuck
 * row), so they take the neutral `queued` styling rather than a green or red
 * that would assert an outcome the row never had.
 */
export function requestStatusFromAttemptStatus(status: RequestSendAttempt['status']): RequestStatus | null {
  switch (status) {
    case 'dispatching': return 'running';
    case 'sent':
    case 'recovered': return 'complete';
    case 'failed': return 'failed';
    case 'already_in_flow':
    case 'cancelled': return null;
  }
}

/** One `enrollment_send_attempt` row's badge — attempt word, mapped colour. */
export function RequestAttemptStatusBadge({ status }: { status: RequestSendAttempt['status'] }) {
  return (
    <RequestStatusBadge
      status={requestStatusFromAttemptStatus(status) ?? 'queued'}
      label={status.replace(/_/g, ' ')}
    />
  );
}

export function RequestStatusBadge({ status, code, label }: { status: RequestStatus; code?: string | null; label?: string }) {
  const unavailable = isOutcomeUnavailable(status, code ?? null);
  const badgeStatus: StatusBadgeStatus = unavailable ? 'warning' : STATUS_TO_BADGE[status];
  const text = unavailable ? 'unknown' : (label ?? status);
  return (
    // Wrapper carries the pinned testid + a semantic data-status hook: Glaze's StatusBadge props are closed (no data-*/aria-* passthrough).
    <span data-testid="request-status-badge" data-status={badgeStatus} className="inline-flex shrink-0 items-center">
      {status === 'running' && label === undefined ? (
        // Prototype: the badge steps aside for a spinner while running; sr-only text keeps the word.
        <><LoadingSpinner size="sm" /><span className="sr-only">running</span></>
      ) : (
        <StatusBadge status={badgeStatus} size="xs" className="capitalize" tooltipContent={unavailable ? UNAVAILABLE_TOOLTIP : undefined}>
          {text}
        </StatusBadge>
      )}
    </span>
  );
}
